import React from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import './PaymentProcessor.css';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

const CheckoutForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = React.useState<string | null>(null);
  const [processing, setProcessing] = React.useState(false);
  const napkinState = useSelector((state: RootState) => state.napkin);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    // Create payment method
    const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardElement)!,
    });

    if (stripeError) {
      setError(stripeError.message || 'An error occurred');
      setProcessing(false);
      return;
    }

    // Send to your server
    try {
      const response = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          amount: calculateTotal(), // Implement this based on your pricing
          napkinDetails: napkinState,
        }),
      });

      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else {
        // Handle successful payment
        window.location.href = '/order-confirmation';
      }
    } catch (err) {
      setError('An error occurred while processing your payment.');
    }

    setProcessing(false);
  };

  const calculateTotal = () => {
    // Implement your pricing logic here
    const basePrice = 1.50;
    const quantity = 50; // This would come from state in real implementation
    return basePrice * quantity * 100; // Convert to cents for Stripe
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <div className="form-row">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </div>
      {error && <div className="error-message">{error}</div>}
      <button 
        type="submit" 
        disabled={!stripe || processing}
        className="payment-button"
      >
        {processing ? 'Processing...' : 'Pay Now'}
      </button>
    </form>
  );
};

const PaymentProcessor: React.FC = () => {
  const napkinState = useSelector((state: RootState) => state.napkin);
  const checkoutState = useSelector((state: RootState) => state.checkout);
  const total = checkoutState.basePrice * checkoutState.quantity;

  return (
    <Elements stripe={stripePromise}>
      <div className="payment-container">
        <div className="payment-header">
          <h1>Secure Checkout</h1>
          <div className="security-badges">
            <span className="security-badge">🔒 256-bit SSL Encryption</span>
            <span className="security-badge">✓ PCI DSS Compliant</span>
            <span className="security-badge">🛡️ Secure Payments</span>
          </div>
        </div>

        <div className="payment-content">
          {/* Order Summary */}
          <div className="order-summary-card">
            <h3>Order Summary</h3>
            <div className="order-item">
              <div className="item-details">
                <div className="item-name">Custom Napkins</div>
                <div className="item-specs">
                  {napkinState.color} · {napkinState.foilColor}
                  {napkinState.textLineOne && ` · "${napkinState.textLineOne}"`}
                </div>
              </div>
              <div className="item-price">
                {checkoutState.quantity} × ${checkoutState.basePrice.toFixed(2)}
              </div>
            </div>
            
            <div className="order-total">
              <span className="total-label">Total</span>
              <span className="total-amount">${total.toFixed(2)}</span>
            </div>

            <div className="trust-indicators">
              <div className="trust-item">
                <svg viewBox="0 0 24 24">
                  <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
                </svg>
                <span>Money Back Guarantee</span>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <div className="payment-form-card">
            <h2>Payment Information</h2>
            <div className="form-section">
              <div className="form-section-title">Card Details</div>
              <CheckoutForm />
            </div>
            
            <div className="trust-indicators">
              <div className="trust-item">
                <svg viewBox="0 0 24 24">
                  <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/>
                </svg>
                <span>Cancel anytime</span>
              </div>
              <div className="trust-item">
                <svg viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span>100% Secure</span>
              </div>
              <div className="trust-item">
                <svg viewBox="0 0 24 24">
                  <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 00-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z"/>
                </svg>
                <span>Fast Delivery</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Elements>
  );
};

export default PaymentProcessor;
